import { BACKEND_URLS } from '@/config/urls';
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  Text,
  Badge,
} from '@chakra-ui/react';
import { Icon, IconName, Tile } from '@meritcircle/ui/components';
import useTranslation from 'next-translate/useTranslation';
import {
  ClaimRewardTileProps,
  TilesOverviewProps,
  TileFromReward,
  RewardTileProps,
} from './tiles-overview.types';
import NextLink from 'next/link';
import { useCurrentUser } from '@/utils/hooks/useCurrentUser';
import { useCountdown } from '@/utils/hooks/useCountDown';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { useRouter } from 'next/router';
import { BeamRainbow, isExternalUrl } from '@meritcircle/ui';
import { useState, useMemo } from 'react';
import { rem } from 'polished';
import { Raffle, RaffleStatus } from '@/utils/api/quests/schemas';

const isTile = (item: TileFromReward | ClaimRewardTileProps): item is TileFromReward =>
  (item as TileFromReward).href !== undefined;

export const TilesOverview = ({ items }: TilesOverviewProps) => {
  const [isHoverOnGridItem, setIsHoverOnGridItem] = useState(false);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      // Keep claim reward tile first
      if (!isTile(a)) return -1;
      if (!isTile(b)) return 1;

      const aRaffles = a.raffles || [];
      const bRaffles = b.raffles || [];

      // Check for live raffles
      const aHasLive = aRaffles.some(
        r => r.status === RaffleStatus.live && new Date(r.active_since) < new Date(),
      );
      const bHasLive = bRaffles.some(
        r => r.status === RaffleStatus.live && new Date(r.active_since) < new Date(),
      );
      if (aHasLive && !bHasLive) return -1;
      if (!aHasLive && bHasLive) return 1;

      // Check for upcoming raffles
      const aHasUpcoming = aRaffles.some(
        r =>
          r.status === RaffleStatus.not_started ||
          (r.active_since && new Date(r.active_since) > new Date()),
      );
      const bHasUpcoming = bRaffles.some(
        r =>
          r.status === RaffleStatus.not_started ||
          (r.active_since && new Date(r.active_since) > new Date()),
      );
      if (aHasUpcoming && !bHasUpcoming) return -1;
      if (!aHasUpcoming && bHasUpcoming) return 1;

      return 0;
    });
  }, [items]); // Only re-sort when items change

  return (
    <Grid
      templateColumns={{
        base: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
      }}
      gap={{ base: 2, md: 1 }}
      sx={{
        '& > div:hover': { opacity: 1 },
        '& > div:not(:hover)': { opacity: isHoverOnGridItem ? 0.7 : 1 },
        '& > div': {
          transition: 'opacity',
          transitionDuration: 'fast',
          transitionTimingFunction: 'ease-out',
        },
      }}>
      {sortedItems.map((item, index) => (
        <GridItem key={`${index}`} opacity={!setIsHoverOnGridItem ? 1 : 'unset'}>
          <AspectRatio ratio={424 / 650} w="100%">
            {isTile(item) ? (
              <RewardTile {...item} setHovering={setIsHoverOnGridItem} />
            ) : (
              <ClaimRewardTile
                {...item}
                subtitle={item.subtitle}
                setHovering={setIsHoverOnGridItem}
              />
            )}
          </AspectRatio>
        </GridItem>
      ))}
    </Grid>
  );
};

const RewardTile = ({ millisecondsUntilEnd, setHovering, ...props }: RewardTileProps) => {
  const { t } = useTranslation('common');
  const { hasExpired, countdown } = useCountdown(millisecondsUntilEnd);
  const subtitle =
    props.subtitle || !hasExpired
      ? `${countdown.days} ${t('date.days')} ${countdown.hours}:${countdown.minutes}:${
          countdown.seconds
        }`
      : '';

  const raffles = props.raffles || [];

  const liveRaffles = raffles
    .filter(r => r.status === RaffleStatus.live)
    .filter(r => new Date(r.active_since) < new Date())
    .sort((a, b) => new Date(a.active_since).getTime() - new Date(b.active_since).getTime());

  const incomingRaffles = raffles.filter(
    r =>
      r.status === RaffleStatus.not_started ||
      (r.active_since && new Date(r.active_since) > new Date()),
  );

  const endedRaffles = raffles.filter(r => r.status === RaffleStatus.ended);

  return (
    <Box onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <Box
        position="absolute"
        bottom={{ base: 'unset', md: 4 }}
        top={{ base: 4, md: 'unset' }}
        right={4}
        zIndex={1}>
        {liveRaffles.length > 0 ? (
          <Badge
            bg="#E8BBFB"
            backdropFilter="blur(20px)"
            color="#710036"
            borderRadius="full"
            px={3}
            py={1.5}
            display="flex"
            alignItems="center"
            gap={1}
            fontSize="sm">
            <Box as="span" w={2} h={2} borderRadius="full" bg="#FF0F81" />
            {`${liveRaffles.length} raffles live`}
          </Badge>
        ) : (
          <Badge
            bg="rgba(255, 255, 255, 0.2)"
            backdropFilter="blur(14px)"
            color="white"
            borderRadius="full"
            px={3}
            py={1.5}
            fontSize="sm">
            No raffles available
          </Badge>
        )}
        {incomingRaffles.length > 0 ? (
          <Text fontSize={10} fontWeight={300} textAlign="right">
            {`+${incomingRaffles.length} upcoming raffle`}
          </Text>
        ) : endedRaffles.length > 0 ? (
          <Text fontSize={10} fontWeight={300} textAlign="right">
            {`+${endedRaffles.length} raffles ended`}
          </Text>
        ) : (
          <Box fontWeight={300} textAlign="right" height="18px" />
        )}
      </Box>
      <Tile {...props} subtitle={subtitle} h="100%" />
    </Box>
  );
};

const ClaimRewardTile = ({ title, subtitle, setHovering }: ClaimRewardTileProps) => {
  const { t } = useTranslation('common');
  const currentUser = useCurrentUser();
  const isLoggedIn = !!currentUser?.id;
  const { redirect } = useRedirect();
  const router = useRouter();

  return (
    <Box
      overflow="hidden"
      position="relative"
      background="linear-gradient(180deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0) 59.36%, rgba(0, 0, 0, 0.55) 100%)"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="claim-reward-tile">
      <Box
        position="absolute"
        bottom="0"
        right="0"
        width="100%"
        transform={{ base: 'translate(0, 10%)', md: 'translate(0, 0%)' }}>
        <Box sx={{ svg: { width: '100%', height: '100%' } }}>
          <BeamRainbow />
        </Box>
      </Box>
      <Grid
        height="100%"
        gridTemplateRows="1fr max-content 1fr"
        gridTemplateColumns="1fr"
        textAlign="center"
        justifyContent="center">
        <GridItem display="flex" alignItems="center" justifyContent="center" py="space.16">
          <Flex flexDirection="column">
            <Heading as="h3" size="h1" color="white" lineHeight={1}>
              {title}
            </Heading>
            <Text fontSize={{ base: rem(16), md: rem(18) }} fontWeight={600}>
              {subtitle}
            </Text>
          </Flex>
        </GridItem>
        <GridItem>
          <Box>
            {isLoggedIn ? (
              <Button
                as="div"
                size={{ base: 'xs', sm: 'md' }}
                pointerEvents="none"
                boxShadow="none"
                rightIcon={<Icon icon={IconName.checkMark} />}
                variant="outline"
                border="none"
                backgroundColor={'#19AD54'}>
                {t('labels.claimed')}
              </Button>
            ) : (
              <NextLink href={BACKEND_URLS.REGISTER} passHref legacyBehavior>
                <Button
                  as={Link}
                  variant="outline"
                  size={{ base: 'xs', md: 'md' }}
                  isExternal={isExternalUrl(BACKEND_URLS.REGISTER)}
                  onClick={() => {
                    redirect(BACKEND_URLS.REGISTER, router.asPath);
                  }}>
                  {t('labels.register-to-claim')}
                </Button>
              </NextLink>
            )}
          </Box>
        </GridItem>
        <GridItem></GridItem>
      </Grid>
    </Box>
  );
};
